import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QrCode, CheckCircle2, XCircle, AlertTriangle, Clock, Users, Calendar, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { useAuth } from "../auth";
import * as apiClient from "../api";

const STATE = {
  VALID: { c: "text-emerald-600", bg: "bg-emerald-50", icon: CheckCircle2, label: "VALIDO" },
  ALREADY_USED: { c: "text-amber-600", bg: "bg-amber-50", icon: AlertTriangle, label: "GIÀ UTILIZZATO" },
  CANCELLED: { c: "text-red-500", bg: "bg-red-50", icon: XCircle, label: "ANNULLATO" },
  INVALID: { c: "text-red-500", bg: "bg-red-50", icon: XCircle, label: "NON VALIDO" },
};

export default function QRValidate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    try {
      const r = await apiClient.validateVoucher(code.trim().toUpperCase());
      setResult(r);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore validazione");
      setResult({ status: "INVALID" });
    }
  };

  const checkin = async () => {
    try {
      await apiClient.checkinVoucher(result.booking_number);
      toast.success("Check-in effettuato!");
      lookup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore check-in");
    }
  };

  const st = result ? STATE[result.status] : null;

  return (
    <div className="min-h-screen bg-[#0A1128] text-white">
      <header className="px-6 h-16 flex items-center justify-between border-b border-white/10">
        <Logo light />
        <div className="flex items-center gap-3 text-sm text-white/60">
          <span>{user?.email}</span>
          <button onClick={() => navigate(user?.role === "operator" ? "/operatore" : "/gestione")} className="flex items-center gap-1 hover:text-white"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
        </div>
      </header>
      <div className="max-w-lg mx-auto px-4 py-10">
        <div className="text-center mb-6">
          <QrCode className="h-12 w-12 mx-auto text-secondary mb-2" />
          <h1 className="text-2xl font-heading font-semibold">Validazione Voucher</h1>
          <p className="text-white/50 text-sm">Inserisci o scansiona il numero prenotazione</p>
        </div>
        <form onSubmit={lookup} className="flex gap-2 mb-6">
          <input data-testid="validate-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CRB-XXXXXX-XXXXX" className="flex-1 rounded-xl bg-white/10 border border-white/20 px-4 py-3 outline-none placeholder:text-white/30" />
          <button data-testid="validate-lookup-btn" className="rounded-xl bg-secondary text-white font-semibold px-6">Verifica</button>
        </form>

        {result && st && (
          <div className={`rounded-2xl bg-white text-slate-900 overflow-hidden animate-float-up`} data-testid="validation-result">
            <div className={`${st.bg} p-6 flex items-center gap-3`}>
              <st.icon className={`h-10 w-10 ${st.c}`} />
              <div><div className={`text-xl font-heading font-bold ${st.c}`} data-testid="validation-status">{st.label}</div>{result.booking_number && <div className="text-sm text-slate-500">{result.booking_number}</div>}</div>
            </div>
            {result.items && (
              <div className="p-6 space-y-4">
                <div><div className="text-xs font-bold uppercase text-slate-400">Cliente</div><div className="font-semibold">{result.customer.name} {result.customer.surname} · {result.customer.phone}</div></div>
                {result.items.map((it) => (
                  <div key={`${it.date}-${it.time}-${it.title}`} className="rounded-xl border border-slate-100 p-3">
                    <div className="font-heading font-semibold">{it.title}</div>
                    <div className="flex flex-wrap gap-3 text-sm text-slate-500 mt-1">
                      <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{it.date}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{it.time}</span>
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{it.participants}</span>
                    </div>
                  </div>
                ))}
                {result.checked_in_at && <p className="text-xs text-amber-600">Validato il {new Date(result.checked_in_at).toLocaleString("it-IT")} da {result.checked_in_by}</p>}
                {result.status === "VALID" && (
                  <button onClick={checkin} data-testid="checkin-btn" className="w-full rounded-full bg-primary text-white font-semibold py-3">✓ Check-in / Valida ingresso</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
